import * as React from 'react';
import { useContext } from 'react';
import styled from 'styled-components';

import { isPermitted } from 'util/PermissionsMixin';
import { ControlLabel, Button } from 'components/bootstrap';
import { Markdown } from 'components/common';
import { Row, Col, DataWell } from 'common/components/layout-styles';
import { MarkdownPreview } from 'components/common/MarkdownEditor';
import { useGetEventDefinitionDetailsBatched } from 'security-app/hooks/useSecurityEventsAPI';
import { useModalContext } from 'common/contexts';
import type { SecurityEventAPIType } from 'security-app/hooks/api/securityEventsAPI.types';
import type { EventDefinition } from 'components/event-definitions/event-definitions-types';
import useCurrentUser from 'hooks/useCurrentUser';
import EventDetailsContext from 'security-app/components/SecurityEvents/Alerts/EventDetails/EventDetailsContext';
import { TD } from 'security-app/components/common';
import type { Attributes } from 'stores/PaginationTypes';
import SecurityEventDetailsTable from 'security-app/components/SecurityEvents/Alerts/EventDetails/SecurityEventDetailsTable';
import AssocAssetsWidget from 'security-app/components/Assets/plugin/AssocAssetsWidget';
import EventProcedureSummary from 'security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureSummary';

import DetectionChain from './DetectionChain';

type SigmaEventDefinitionConfig = EventDefinition['config'] & {
  sigma_rule_id: string;
};

export const LEFT_COLUMN_WIDTH = '52%';
export const RIGHT_COLUMN_WIDTH = '48%';

const DetailsBox = styled(Col)`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.input.border};
`;

const StyledControlLabel = styled(ControlLabel)`
  margin: 0;
  white-space: nowrap;
`;

type Props = {
  securityEvent: SecurityEventAPIType;
  orientation?: 'horz' | 'vert';
  detailsAttributes: Attributes;
};

function EventDetails({ securityEvent, orientation = 'horz', detailsAttributes }: Props) {
  const RowCol = orientation === 'horz' ? Row : Col;
  const { permissions } = useCurrentUser();
  const { setModal, setEntity } = useModalContext();
  const eventDetailsContext = useContext(EventDetailsContext);
  const { eventDefinition } = useGetEventDefinitionDetailsBatched(securityEvent?.event?.event_definition_id);

  const canManageSecurityEvents = React.useMemo(() => isPermitted(permissions, 'security_event:edit'), [permissions]);

  const showRemediationSteps = React.useMemo(
    () => !!eventDefinition?.remediation_steps && !eventDefinition?.event_procedure,
    [eventDefinition],
  );

  const openMDEditor = () => {
    setEntity(securityEvent);
    setModal('NOTES-EDITOR');
  };

  const editEventDefinition = () => {
    if (eventDefinition.config.type === 'sigma-v1') {
      setEntity((eventDefinition.config as SigmaEventDefinitionConfig).sigma_rule_id);
      setModal('EDIT-SIGMA-EVENT-DEFINITION');
    } else {
      setEntity(eventDefinition);
      setModal('EVENT-DEFINITION');
    }
  };

  const { title: eventDefinitionTitle, event_procedure } = eventDefinition || {
    remediation_steps: '',
    event_procedure: '',
    title: securityEvent.event.event_definition_id,
  };

  return (
    <>
      {securityEvent.event.associated_assets.length > 0 && (
        <Row $align="flex-start">
          <StyledControlLabel>Associated Assets:</StyledControlLabel>
          <AssocAssetsWidget assetIds={securityEvent.event.associated_assets} />
        </Row>
      )}
      {securityEvent.part_of_detection_chain && (
        <Row $align="flex-start">
          <StyledControlLabel>Detection Chain:</StyledControlLabel>
          <DetectionChain eventId={securityEvent.event_id} />
        </Row>
      )}
      <RowCol $align="stretch">
        <Col $width={orientation === 'vert' ? '100%' : LEFT_COLUMN_WIDTH}>
          {eventDefinition && (
            <DetailsBox>
              <Row $justify={eventDetailsContext.hideEventDefinitionEdit ? 'flex-start' : 'space-between'}>
                <strong>Event Definition</strong>
                {canManageSecurityEvents && !eventDetailsContext.hideEventDefinitionEdit ? (
                  <Button bsSize="small" bsStyle="primary" onClick={editEventDefinition}>
                    Edit
                  </Button>
                ) : (
                  <TD />
                )}
              </Row>
              <span>{eventDefinitionTitle}</span>
            </DetailsBox>
          )}
          <DetailsBox style={{ flexGrow: 1 }}>
            <Row $justify={eventDetailsContext.hideNotesEdit ? 'flex-start' : 'space-between'}>
              <strong>Notes</strong>
              {canManageSecurityEvents && !eventDetailsContext.hideNotesEdit ? (
                <Button bsSize="small" bsStyle="primary" onClick={openMDEditor}>
                  {!securityEvent.notes ? 'Add' : 'Edit'}
                </Button>
              ) : (
                <TD />
              )}
            </Row>
            <Markdown text={securityEvent.notes || '_No notes_'} />
          </DetailsBox>
          <DetailsBox style={{ flexGrow: 2 }}>
            {showRemediationSteps ? (
              <>
                <strong>Remediation Steps</strong>
                <DataWell>
                  <MarkdownPreview
                    value={eventDefinition?.remediation_steps}
                    maxHeight={500}
                    show
                    noBackground
                    noBorder
                    noPadding
                  />
                </DataWell>
              </>
            ) : (
              <>
                <strong>Event Procedure Summary</strong>
                {event_procedure ? (
                  <EventProcedureSummary eventProcedureId={event_procedure} eventId={securityEvent?.event_id} />
                ) : (
                  <i>This Event Definition does not have any Event Procedures yet</i>
                )}
              </>
            )}
          </DetailsBox>
        </Col>
        <Col $width={orientation === 'vert' ? '100%' : RIGHT_COLUMN_WIDTH}>
          <DetailsBox style={{ flexGrow: 1 }}>
            <strong>Details</strong>
            <SecurityEventDetailsTable event={securityEvent} attributesList={detailsAttributes} />
          </DetailsBox>
        </Col>
      </RowCol>
    </>
  );
}

export default EventDetails;
